import React, { useEffect, useState } from "react";
import { BarChart3, Eye, Linkedin, Twitter, Mail, Globe, Briefcase } from "lucide-react";
import api from "@/lib/api";

const SOURCE_META = {
  linkedin: { label: "LinkedIn", icon: Linkedin, color: "bg-[#0A66C2]" },
  twitter: { label: "Twitter / X", icon: Twitter, color: "bg-slate-400" },
  email: { label: "Email", icon: Mail, color: "bg-amber-400" },
  direct: { label: "Direct / Other", icon: Globe, color: "bg-indigo-400" },
  other: { label: "Other", icon: Globe, color: "bg-indigo-400" },
};

export default function PortfolioAnalyticsCard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/users/me/portfolio-analytics").then((res) => setData(res.data)).catch(() => {});
  }, []);

  if (!data) return null;
  const maxSource = Math.max(1, ...Object.values(data.traffic_sources));

  return (
    <div className="glass-panel rounded-2xl p-6 mb-6" data-testid="portfolio-analytics-card">
      <h2 className="font-heading font-semibold text-white mb-1 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-indigo-400" /> Portfolio Analytics</h2>
      <p className="text-xs text-slate-400 mb-4">See who's discovering your public portfolio.</p>

      <div className="grid grid-cols-2 gap-3 mb-5">
        <div className="bg-white/[0.02] rounded-xl p-3 text-center" data-testid="analytics-total-views">
          <div className="flex items-center justify-center gap-1.5 text-white"><Eye className="w-4 h-4 text-indigo-400" /><span className="font-heading text-2xl font-bold">{data.total_views}</span></div>
          <p className="text-[10px] text-slate-400 mt-1">Total Views</p>
        </div>
        <div className="bg-white/[0.02] rounded-xl p-3 text-center" data-testid="analytics-month-views">
          <p className="font-heading text-2xl font-bold text-white">{data.views_this_month}</p>
          <p className="text-[10px] text-slate-400 mt-1">Views (Last 30 Days)</p>
        </div>
      </div>

      {Object.keys(data.traffic_sources).length > 0 && (
        <div className="mb-5" data-testid="analytics-traffic-sources">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2">Traffic Sources</p>
          <div className="space-y-2">
            {Object.entries(data.traffic_sources).sort((a, b) => b[1] - a[1]).map(([source, count]) => {
              const meta = SOURCE_META[source] || SOURCE_META.other;
              const Icon = meta.icon;
              return (
                <div key={source} className="flex items-center gap-2">
                  <Icon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                  <span className="text-xs text-slate-400 w-24 shrink-0">{meta.label}</span>
                  <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden">
                    <div className={`h-full ${meta.color} rounded-full`} style={{ width: `${(count / maxSource) * 100}%` }} />
                  </div>
                  <span className="text-xs text-white font-semibold w-6 text-right">{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {data.most_viewed_projects.length > 0 && (
        <div data-testid="analytics-most-viewed-projects">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2">Most Viewed Case Studies</p>
          <div className="space-y-1.5">
            {data.most_viewed_projects.map((p) => (
              <div key={p.case_study_id} className="flex items-center justify-between gap-2 bg-white/[0.02] rounded-lg px-3 py-2" data-testid={`analytics-project-${p.case_study_id}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <Briefcase className="w-3.5 h-3.5 text-purple-300 shrink-0" />
                  <span className="text-xs text-slate-300 truncate">{p.company}</span>
                </div>
                <span className="text-xs font-bold text-indigo-300 shrink-0">{p.views} view{p.views !== 1 ? "s" : ""}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.total_views === 0 && (
        <p className="text-xs text-slate-400 text-center py-2">No views yet — share your portfolio link to start tracking traffic!</p>
      )}
    </div>
  );
}
